import type { FC } from "react";
import { useForm, useStore } from "@tanstack/react-form";

import { usePatchPatient } from "@/api/patients";
import Edit from "@/assets/icons/edit";
import {
  FieldDatepicker,
  FieldInput,
  FieldRadio,
  FieldSelect,
} from "@/components/form";
import DialogCustom from "@/components/modals/dialog-custom";
import RiskModalFooter from "@/components/modals/profil-risk/risk-modal-footer";
import type { RiskUpdateModalProps } from "@/components/modals/profil-risk/risk-update-modal";
import { Separator } from "@/components/ui/separator";
import {
  patchSmokingDefaultValues,
  patchSmokingSchema,
} from "@/schemas/patient";
import { resetForm } from "@/utils/form";
import { smokingOptions, stateOptions } from "@/utils/patient";

const SmokingModal: FC<RiskUpdateModalProps> = ({
  riskModalOpen,
  setRiskModalOpen,
  patient,
}) => {
  const { mutate } = usePatchPatient(patient.id);

  const { smoking, smokingState, smokingEnd, dailySmokes } = patient;

  const { Field, store, handleSubmit } = useForm({
    defaultValues: {
      smoking,
      smokingState,
      smokingEnd: smokingEnd ?? patchSmokingDefaultValues.smokingEnd,
      dailySmokes: dailySmokes ?? patchSmokingDefaultValues.dailySmokes,
    },
    onSubmit: async ({ value, formApi }) => {
      const values = patchSmokingSchema.parse(value);
      mutate(value, {
        onSuccess: () => {
          setRiskModalOpen("none");
          resetForm(values, formApi);
        },
      });
    },
  });

  const smokingValue = useStore(store, (state) => state.values.smoking);

  return (
    <DialogCustom
      title="Modifier les informations"
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px]">
          <Edit className="w-6 h-6" color="black" />
        </div>
      }
      description={
        <FieldSelect
          Field={Field}
          schema={patchSmokingSchema}
          name="smokingState"
          options={stateOptions}
          validators={{
            onChange: patchSmokingSchema.shape.smokingState,
          }}
          risk={"smoking"}
        />
      }
      open={riskModalOpen !== "none"}
      onOpenChange={() => setRiskModalOpen("none")}
      className="flex flex-col gap-4"
    >
      <Separator />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full px-6">
        <FieldRadio
          Field={Field}
          schema={patchSmokingSchema}
          name="smoking"
          label="Patient fumeur"
          options={smokingOptions}
          orientation="horizontal"
          validators={{
            onChange: patchSmokingSchema.shape.smoking,
          }}
        />
        {smokingValue === "PREVIOUS" ? (
          <div className="flex gap-4">
            <FieldDatepicker
              Field={Field}
              name={"smokingEnd"}
              label="Date d'arrêt"
              schema={patchSmokingSchema}
              validators={{ onChange: patchSmokingSchema.shape.smokingEnd }}
            />
            <FieldInput
              Field={Field}
              schema={patchSmokingSchema}
              name="dailySmokes"
              placeholder="0"
              label="Nombre de cigarettes par jour"
              type="number"
              min={0}
              validators={{
                onChange: patchSmokingSchema.shape.dailySmokes,
              }}
            />
          </div>
        ) : null}
        {smokingValue === "TRUE" ? (
          <div className="flex gap-4">
            <FieldInput
              Field={Field}
              schema={patchSmokingSchema}
              name="dailySmokes"
              placeholder="0"
              label="Nombre de cigarettes par jour"
              type="number"
              min={0}
              validators={{
                onChange: patchSmokingSchema.shape.dailySmokes,
              }}
            />
          </div>
        ) : null}
        <input type="submit" className="hidden" />
      </form>
      <Separator />
      <RiskModalFooter
        setRiskModalOpen={setRiskModalOpen}
        handleSubmit={handleSubmit}
      />
    </DialogCustom>
  );
};

export default SmokingModal;
